import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { toastEmailDispatch } from '@/lib/emailDispatchToast';

import { v1Post } from '@/lib/api';
import {
  splitEmails,
  isValidEmail,
  emailsEqual,
  panelOverlapsCandidate,
  toDatetimeLocalValue,
  isDatetimeLocalInPast,
} from '@/lib/panelInterviewForm';
import { CANDIDATE_ON_PANEL_MSG } from '@/components/panelInterviews/panelInterviewConstants';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export function SchedulePanelDialog({
  open, onOpenChange, candidateId, candidateEmail, panelKind, copy, evaluationId,
  vacantRounds = [],
}) {
  const qc = useQueryClient();
  const initialStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    return toDatetimeLocalValue(d);
  }, []);
  const initialEnd = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(11, 0, 0, 0);
    return toDatetimeLocalValue(d);
  }, []);

  const [subject, setSubject] = useState(copy.defaultSubject);
  const [description, setDescription] = useState(copy.defaultDescription);
  const [startAt, setStartAt] = useState(initialStart);
  const [endAt, setEndAt] = useState(initialEnd);
  const [panelEmailsRaw, setPanelEmailsRaw] = useState('');
  const [pipelineStageId, setPipelineStageId] = useState('');

  useEffect(() => {
    if (!open) return;
    if (panelKind === 'tech' && vacantRounds.length) {
      setPipelineStageId(vacantRounds[0].id);
      if (vacantRounds[0].label) setSubject(vacantRounds[0].label);
    } else {
      setPipelineStageId('');
    }
  }, [open, panelKind, vacantRounds]);

  const panelEmails = useMemo(() => splitEmails(panelEmailsRaw), [panelEmailsRaw]);
  const invalidEmails = panelEmails.filter((e) => !isValidEmail(e));
  const candidateOnPanel = panelOverlapsCandidate(panelEmails, candidateEmail);
  const startInPast = isDatetimeLocalInPast(startAt);
  const minStart = toDatetimeLocalValue(new Date());
  const minEnd = startAt && startAt > minStart ? startAt : minStart;
  const canSubmit =
    !!evaluationId &&
    subject.trim() &&
    candidateEmail &&
    panelEmails.length > 0 &&
    invalidEmails.length === 0 &&
    !startInPast &&
    new Date(endAt) > new Date(startAt) &&
    (panelKind !== 'tech' || !!pipelineStageId);

  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        candidate_id: candidateId,
        evaluation_id: evaluationId,
        subject: subject.trim(),
        description: description.trim() || null,
        start_at: new Date(startAt).toISOString(),
        end_at: new Date(endAt).toISOString(),
        timezone_label: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        candidate_email: candidateEmail,
        panel_emails: panelEmails,
        panel_kind: panelKind,
      };
      if (panelKind === 'tech') body.pipeline_stage_id = pipelineStageId;
      return v1Post('/panels', body);
    },
    onSuccess: (panel) => {
      toast.success(copy.successToast);
      toastEmailDispatch(panel?.email_dispatch);
      qc.invalidateQueries({ queryKey: ['v1-panels', candidateId, panelKind] });
      // PipelineTimeline reads panels from candidate hydrate, not v1-panels.
      qc.invalidateQueries({ queryKey: ['v1-candidate', candidateId] });
      qc.invalidateQueries({ queryKey: ['v1-candidate-activity', candidateId] });
      onOpenChange(false);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      const msg =
        (detail && typeof detail === 'object' && detail.message) ||
        (typeof detail === 'string' ? detail : null) ||
        'Could not schedule meeting.';
      toast.error(msg);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid={copy.testIdDialog}>
        <DialogHeader>
          <DialogTitle>{copy.scheduleTitle}</DialogTitle>
          <DialogDescription>{copy.scheduleDescription}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {panelKind === 'tech' && vacantRounds.length > 1 && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">Pipeline round</label>
              <Select
                value={pipelineStageId}
                onValueChange={(v) => {
                  setPipelineStageId(v);
                  const round = vacantRounds.find((r) => r.id === v);
                  if (round?.label) setSubject(round.label);
                }}
              >
                <SelectTrigger data-testid="panel-round-select">
                  <SelectValue placeholder={vacantRounds.length ? 'Select round' : 'No vacant rounds'} />
                </SelectTrigger>
                <SelectContent>
                  {vacantRounds.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.label || r.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {panelKind === 'tech' && vacantRounds.length === 0 && (
            <p className="text-[11px] text-amber-700">
              All panel rounds are occupied. Cancel an existing interview to free a slot.
            </p>
          )}
          {panelKind === 'tech' && vacantRounds.length === 1 && (
            <p className="text-[11px] text-muted-foreground" data-testid="panel-round-fixed">
              Round: {vacantRounds[0].label || 'Panel interview'}
            </p>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="panel-subject-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Agenda / notes (HTML or plain text)</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} data-testid="panel-description-input" />
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Start</label>
              <Input
                type="datetime-local"
                value={startAt}
                min={minStart}
                onChange={(e) => setStartAt(e.target.value)}
                data-testid="panel-start-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">End</label>
              <Input
                type="datetime-local"
                value={endAt}
                min={minEnd}
                onChange={(e) => setEndAt(e.target.value)}
                data-testid="panel-end-input"
              />
            </div>
          </div>
          {startInPast && (
            <p className="text-xs text-destructive" data-testid="panel-start-in-past-error">
              Interview cannot be scheduled in the past.
            </p>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Candidate email</label>
            <Input value={candidateEmail || ''} disabled data-testid="panel-candidate-email" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {copy.membersLabel} <span className="text-muted-foreground">(comma or newline separated)</span>
            </label>
            <Textarea
              rows={3}
              value={panelEmailsRaw}
              onChange={(e) => setPanelEmailsRaw(e.target.value)}
              placeholder="hr@kadellabs.com, manager@kadellabs.com"
              data-testid="panel-emails-input"
            />
            <div className="mt-1 flex flex-wrap gap-1.5">
              {panelEmails.map((email) => (
                <Badge
                  key={email}
                  variant={
                    !isValidEmail(email) || emailsEqual(email, candidateEmail)
                      ? 'destructive'
                      : 'secondary'
                  }
                >
                  {email}
                </Badge>
              ))}
            </div>
            {invalidEmails.length > 0 && (
              <p className="mt-1 text-xs text-destructive">Invalid email(s): {invalidEmails.join(', ')}</p>
            )}
            {candidateOnPanel && (
              <p className="mt-1 text-xs text-destructive" data-testid="panel-candidate-on-panel-error">
                {CANDIDATE_ON_PANEL_MSG}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="panel-cancel-button">Cancel</Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit || mutation.isPending}
            data-testid="panel-schedule-submit"
            className="gap-1.5"
          >
            <Calendar className="h-4 w-4" />
            {mutation.isPending ? 'Scheduling…' : 'Schedule Meeting'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
